import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  TrendingUp, TrendingDown, Users, 
  ChevronRight, RefreshCcw, Info, Eye, AlertTriangle,
  CheckCircle, Clock, Ban, Calculator, Percent,
  Award, ShieldAlert, Minus
} from 'lucide-react';
import AEDIcon from './icons/AEDIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Commission Automation Admin Panel
 * Phase 1: Shadow Mode - Internal Admin Only
 * 
 * Connects ARS to commission payout rules
 */
const CommissionAdminPanel = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentSummary, setAgentSummary] = useState(null);
  const [pendingCommissions, setPendingCommissions] = useState([]);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch overview
      const overviewRes = await fetch(`${API_URL}/api/admin/commission/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const overviewData = await overviewRes.json();
      if (overviewData.success) {
        setOverview(overviewData.data);
      }

      // Fetch agents
      const agentsRes = await fetch(`${API_URL}/api/admin/commission/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const agentsData = await agentsRes.json();
      if (agentsData.success) {
        setAgents(agentsData.data.agents || []);
      }

      // Fetch pending
      const pendingRes = await fetch(`${API_URL}/api/admin/commission/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pendingData = await pendingRes.json();
      if (pendingData.success) {
        setPendingCommissions(pendingData.data.commissions || []);
      }

      // Fetch rates
      const ratesRes = await fetch(`${API_URL}/api/admin/commission/rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ratesData = await ratesRes.json();
      if (ratesData.success) {
        setRates(ratesData.data);
      }
    } catch (err) {
      setError('Error connecting to Commission service');
    }
    setLoading(false);
  };

  const fetchAgentSummary = async (agentId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/commission/agents/${agentId}/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setAgentSummary(data.data);
        setSelectedAgent(agentId);
      }
    } catch (err) {
      console.error('Error fetching agent summary:', err);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'PREMIUM': return 'bg-green-600/20 text-green-400 border-green-600/30';
      case 'STANDARD': return 'bg-blue-600/20 text-blue-400 border-blue-600/30';
      case 'REDUCED': return 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30';
      case 'PENALTY': return 'bg-red-600/20 text-red-400 border-red-600/30';
      default: return 'bg-gray-600/20 text-gray-400 border-gray-600/30';
    }
  };

  const getARSTierColor = (tier) => {
    switch (tier) {
      case 'TIER_A': return 'text-green-400';
      case 'TIER_B': return 'text-blue-400';
      case 'TIER_C': return 'text-yellow-400';
      case 'RESTRICTED': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'PREMIUM': return <Award className="w-4 h-4 text-green-400" />;
      case 'STANDARD': return <CheckCircle className="w-4 h-4 text-blue-400" />;
      case 'REDUCED': return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'PENALTY': return <ShieldAlert className="w-4 h-4 text-red-400" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-8 text-center">
          <RefreshCcw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading Commission Data...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card border-red-600/30">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-400" />
          <p className="text-red-400">{error}</p>
          <Button onClick={fetchData} className="mt-4">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display text-primary flex items-center gap-2">
            <AEDIcon className="w-6 h-6" />
            Commission Automation
          </h2>
          <p className="text-sm text-muted-foreground">Phase 1: Shadow Mode - ARS-Connected Payouts</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2">
          <RefreshCcw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Phase Banner */}
      <Card className="glass-card border-emerald-600/30 bg-emerald-600/5">
        <CardContent className="p-4 flex items-center gap-4">
          <Info className="w-5 h-5 text-emerald-400" />
          <div>
            <p className="text-emerald-400 font-medium">Shadow Mode Active</p>
            <p className="text-xs text-muted-foreground">
              Commission adjustments calculated based on ARS tier. Tier A: +10% bonus | Tier C: -15% | Restricted: -30%
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {['overview', 'agents', 'pending'].map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab)}
            className="capitalize"
          >
            {tab}
          </Button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="glass-card border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-primary">
                  {overview?.total_commissions_calculated || 0}
                </div>
                <p className="text-xs text-muted-foreground">Total Commissions</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-green-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-display text-green-400">
                  {formatCurrency(overview?.total_agent_payouts)}
                </div>
                <p className="text-xs text-muted-foreground">Agent Payouts</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-blue-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-display text-blue-400">
                  {formatCurrency(overview?.total_platform_fees)}
                </div>
                <p className="text-xs text-muted-foreground">Platform Fees</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-yellow-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-display text-yellow-400">
                  {overview?.pending_review_count || 0}
                </div>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </CardContent>
            </Card>
          </div>

          {/* ARS Impact */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                ARS Impact on Commissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded bg-green-600/10 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <span className="text-2xl font-display text-green-400">
                      {formatCurrency(overview?.total_ars_bonuses)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">Total Bonuses (Tier A)</p>
                </div>
                <div className="p-4 rounded bg-red-600/10 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <TrendingDown className="w-4 h-4 text-red-400" />
                    <span className="text-2xl font-display text-red-400">
                      {formatCurrency(overview?.total_ars_penalties)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">Total Penalties (Tier C/Restricted)</p>
                </div>
                <div className="p-4 rounded bg-secondary/20 text-center">
                  <div className="text-2xl font-display text-foreground">
                    {formatCurrency(overview?.net_ars_impact)}
                  </div>
                  <p className="text-xs text-muted-foreground">Net ARS Impact</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tier Distribution */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-primary" />
                Commission Tier Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4">
                {['PREMIUM', 'STANDARD', 'REDUCED', 'PENALTY', 'UNSCORED'].map((tier) => (
                  <div key={tier} className={`p-4 rounded border ${getTierColor(tier)} text-center`}>
                    <div className="flex items-center justify-center gap-1 mb-1">
                      {getTierIcon(tier)}
                      <span className="text-xl font-display">
                        {overview?.tier_distribution?.[tier]?.count || 0}
                      </span>
                    </div>
                    <p className="text-xs capitalize">{tier.toLowerCase()}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(overview?.tier_distribution?.[tier]?.total)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Commission Rates */}
          {rates && (
            <Card className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-primary" />
                  Current Commission Rates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium mb-3">Base Rates</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Commission Rate</span>
                        <span className="text-primary">{(rates.base_commission_rate * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Platform Fee</span>
                        <span>{(rates.platform_fee_rate * 100).toFixed(0)}% of commission</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Min Commission</span>
                        <span>{formatCurrency(rates.min_commission)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-3">ARS Multipliers</p>
                    <div className="space-y-2 text-sm">
                      {Object.entries(rates.ars_multipliers).map(([tier, mult]) => (
                        <div key={tier} className="flex justify-between">
                          <span className={getARSTierColor(tier)}>{tier}</span>
                          <span className={mult > 1 ? 'text-green-400' : mult < 1 ? 'text-red-400' : ''}>
                            {mult}x {mult > 1 ? `(+${((mult-1)*100).toFixed(0)}%)` : mult < 1 ? `(${((mult-1)*100).toFixed(0)}%)` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Agents Tab */}
      {activeTab === 'agents' && (
        <div className="grid grid-cols-3 gap-6">
          {/* Agents List */}
          <Card className="glass-card border-white/10 col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Agent Commission Status
                <Badge variant="secondary" className="ml-auto">{agents.length} Agents</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {agents.map((agent) => (
                  <div
                    key={agent.agent_id}
                    onClick={() => fetchAgentSummary(agent.agent_id)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedAgent === agent.agent_id 
                        ? 'border-primary bg-primary/10' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    data-testid={`agent-row-${agent.agent_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getTierColor(agent.commission_tier)}`}>
                          {getTierIcon(agent.commission_tier)}
                        </div>
                        <div>
                          <p className="font-medium">{agent.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{agent.email}</p>
                          <p className="text-xs">
                            <span className={getARSTierColor(agent.ars_tier)}>{agent.ars_tier}</span>
                            <span className="text-muted-foreground"> • {agent.total_deals} deals</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium">{formatCurrency(agent.total_earned)}</p>
                          <p className={`text-xs ${agent.net_ars_impact >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {agent.net_ars_impact >= 0 ? '+' : ''}{formatCurrency(agent.net_ars_impact)} ARS impact
                          </p>
                        </div>
                        <Badge className={getTierColor(agent.commission_tier)}>
                          {agent.commission_tier}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                ))}
                
                {agents.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No agents found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Agent Details */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                Agent Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {agentSummary ? (
                <div className="space-y-4">
                  <div className="text-center p-4 rounded-lg bg-secondary/20">
                    <p className="font-medium text-lg">{agentSummary.agent_name || 'Agent'}</p>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <Badge className={getTierColor(agentSummary.commission_tier)}>
                        {agentSummary.commission_tier}
                      </Badge>
                      <span className={`text-sm ${getARSTierColor(agentSummary.current_ars_tier)}`}>
                        {agentSummary.current_ars_tier}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 rounded bg-secondary/10 text-center">
                      <div className="text-lg font-display text-primary">{agentSummary.total_deals}</div>
                      <p className="text-xs text-muted-foreground">Total Deals</p>
                    </div>
                    <div className="p-3 rounded bg-secondary/10 text-center">
                      <div className="text-lg font-display text-primary">{agentSummary.period_deals}</div>
                      <p className="text-xs text-muted-foreground">90-Day Deals</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Earned</span>
                      <span className="text-green-400">{formatCurrency(agentSummary.total_commission_earned)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Bonuses</span>
                      <span className="text-green-400">+{formatCurrency(agentSummary.total_bonuses)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Penalties</span>
                      <span className="text-red-400">-{formatCurrency(agentSummary.total_penalties)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium border-t border-white/10 pt-2">
                      <span>Net ARS Impact</span>
                      <span className={agentSummary.net_adjustment_impact >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {agentSummary.net_adjustment_impact >= 0 ? '+' : ''}{formatCurrency(agentSummary.net_adjustment_impact)}
                      </span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <p className="text-sm font-medium mb-2">Pending</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{agentSummary.pending_count} commissions</span>
                      <span className="text-yellow-400">{formatCurrency(agentSummary.pending_commissions)}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <p className="text-sm font-medium mb-2">Projections</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Monthly Commission</span>
                      <span>{formatCurrency(agentSummary.projected_monthly_commission)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">ARS Impact/Month</span>
                      <span className={agentSummary.projected_ars_impact >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {agentSummary.projected_ars_impact >= 0 ? '+' : ''}{formatCurrency(agentSummary.projected_ars_impact)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <AEDIcon className="w-8 h-8 mx-auto mb-4 opacity-50" />
                  <p>Select an agent to view details</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pending Tab */}
      {activeTab === 'pending' && (
        <Card className="glass-card border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              Pending Commissions
              <Badge variant="secondary" className="ml-auto">{pendingCommissions.length} Pending</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingCommissions.length > 0 ? (
              <div className="space-y-3">
                {pendingCommissions.map((comm) => (
                  <div
                    key={comm.deal_id}
                    className="p-4 rounded-lg border border-yellow-600/30 bg-yellow-600/5"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Deal: {comm.deal_id?.slice(0, 8)}...</p>
                        <p className="text-sm text-muted-foreground">Agent: {comm.agent_name || comm.agent_id}</p>
                        <p className="text-xs text-muted-foreground">
                          Status: <span className="text-yellow-400">{comm.status}</span>
                          {comm.requires_review && <span className="text-red-400 ml-2">• Needs Review</span>}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-display text-primary">{formatCurrency(comm.net_commission)}</p>
                        <p className="text-xs text-muted-foreground">
                          ARS: {comm.ars_tier} ({comm.ars_multiplier}x)
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" variant="outline" className="text-green-400 border-green-600/30">
                            <CheckCircle className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-400 border-red-600/30">
                            <Ban className="w-3 h-3 mr-1" /> Hold
                          </Button>
                        </div>
                      </div>
                    </div>
                    {comm.review_reason && (
                      <p className="text-xs text-orange-400 mt-2">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        {comm.review_reason}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle className="w-8 h-8 mx-auto mb-4 text-green-400 opacity-50" />
                <p>No pending commissions</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CommissionAdminPanel;
